import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { getGoogleSearchToken } from '../utils/api';

// Define the custom element for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'gen-search-widget': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        configId: string;
        triggerId: string;
        authToken?: string;
      };
      'style': React.DetailedHTMLProps<React.StyleHTMLAttributes<HTMLStyleElement>, HTMLStyleElement>;
    }
  }
}

const GoogleSearchWidget = () => {
  const { isAuthenticated } = useAuth();
  const widgetRef = useRef<HTMLElement>(null);
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchToken = async () => {
      if (!isAuthenticated) return;

      setLoading(true);
      setError(null);

      try {
        const response = await getGoogleSearchToken();
        if (!response.ok) {
          const errorData = await response.json();
          console.error('[Google Search Widget] Backend error:', errorData);
          const errorMessage = errorData.error || 'Failed to fetch Google Search token.';
          const fixMessage = errorData.fix ? ` Fix: ${errorData.fix}` : '';
          throw new Error(`${errorMessage}${fixMessage}`);
        }
        const data = await response.json();
        console.log('[Google Search Widget] Token received successfully');
        setGoogleToken(data.token);
      } catch (err: any) {
        console.error('[Google Search Widget] Error:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, [isAuthenticated]);
  
  useEffect(() => {
    if (googleToken && widgetRef.current) {
      const searchWidget = widgetRef.current as any;
      searchWidget.authToken = googleToken;
    }
  }, [googleToken]);

  if (!isAuthenticated) {
    return <Alert severity="warning">Please log in to use the search functionality.</Alert>;
  }
  
  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <style>
        {`
          .grecaptcha-badge { 
            visibility: hidden;
          }
        `}
      </style>
      <Typography variant="h6" gutterBottom>
        Google Cloud Search
      </Typography>
      <Typography paragraph>
        Use the search widget below to query documents from the connected data store.
      </Typography>
      
      {/* The element that will trigger the widget to open */}
      <Button id="searchWidgetTrigger" variant="contained">Search here</Button>

      {/* The search widget itself */}
      <gen-search-widget
        ref={widgetRef}
        configId="dbdf1e53-03a1-447d-9479-b75e916d0c28"
        triggerId="searchWidgetTrigger"
      >
      </gen-search-widget>
      
      <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
        This site is protected by reCAPTCHA and the Google&nbsp;
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and&nbsp;
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
      </Typography>
    </Box>
  );
};

export default GoogleSearchWidget; 